import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { fmtCalendarDate, fmtDate } from '../../shared/Panel';
import { planningStatusAppearance, isExpired } from './planningStatus';
import { useStatuses } from './statusDictionary';
import { acceptanceStatusAppearance } from './acceptanceStatus';
import { DEMO_SOURCE_HINT, isDemo, sourceLabel } from './recordSource';
import {
  exchangeStates,
  publicationCaption,
  publicationColor,
  publicationHint,
  publicationLabel,
  STATUS_FAILED,
  STATUS_PENDING,
  STATUS_POSTED,
} from './publicationStatus';
import { forkliftInfoOf, forkliftSummary } from './forklift';

/**
 * Komórki listy banku zleceń.
 *
 * ## Dlaczego osobny plik od `UserOfferList.tsx`
 *
 * Te komponenty nie potrzebują react-admina — biorą rekord w propsie i nic
 * więcej. Trzymane razem z `<List>` ciągnęły za sobą cały react-admin przy
 * imporcie, przez co ich test w ogóle się nie uruchamiał (`react-router/dom`
 * nie rozwiązuje się w konfiguracji Jesta z CRA). Rozdzielenie jest
 * jednocześnie lepszą konstrukcją: prezentacja osobno, spięcie z ramą osobno.
 *
 * Rekord opisujemy własnym, luźnym typem zamiast `RaRecord` — te komórki mają
 * działać na tym, co przyszło z API, a nie znać ramy, w której wiszą.
 */

/** Rekord z odpowiedzi API — luźny celowo, bo pola bywają puste. */
export type LooseRecord = Record<string, unknown>;

const asRecord = (value: unknown): LooseRecord | undefined =>
  value && typeof value === 'object' ? (value as LooseRecord) : undefined;

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * Etykieta stanu łamana na dwie linie, gdy kolumnie brakuje miejsca — tylko
 * wtedy; przy wolnym miejscu chip wygląda jak dotąd. Smoke stagingu
 * 19.10.2026: „Obsadzone przewoźnikiem” (ok. 175 px w jednej linii) w widoku
 * Trade przewijało listę tradera przy 1366 px. Bez skracania — pełna nazwa
 * stanu zostaje.
 */
const WRAPPING_STATUS_CHIP = {
  height: 'auto',
  minHeight: 24,
  '& .MuiChip-label': { whiteSpace: 'normal', py: '2px', lineHeight: 1.25 },
} as const;

/**
 * Stan planowania ze znacznikiem ryzyka.
 *
 * Dwie osie w jednej komórce, ale **nie zlane w jedną wartość**: zlecenie
 * obsadzone i zagrożone pokazuje się jako „Obsadzone flotą" z ostrzeżeniem,
 * nie jako sam alert. Gdyby ryzyko było stanem, planista straciłby informację,
 * czy auto nadal stoi pod zlecenie.
 */
export const PlanningStatusChip = ({ record }: { record: LooseRecord }) => {
  // Etykieta ze słownika klienta; kolor z kodu — patrz `planningStatusAppearance`.
  const states = useStatuses();
  const appearance = planningStatusAppearance(record.planningStatus, states);
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Chip size="small" color={appearance.color} label={appearance.label} sx={WRAPPING_STATUS_CHIP} />
      {record.atRisk ? (
        <Tooltip title={asText(record.riskReason) ?? 'Zgłoszone ryzyko'}>
          <WarningAmberIcon fontSize="small" color="error" />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/** Oś akceptacji — osobna od planowania. */
export const AcceptanceStatusChip = ({ record }: { record: LooseRecord }) => {
  const appearance = acceptanceStatusAppearance(record.acceptanceStatus);
  return (
    <Chip
      size="small"
      label={appearance.label}
      sx={{ color: appearance.textColor, backgroundColor: appearance.backgroundColor }}
    />
  );
};

/** Kierunek przewozu. Brak jednego końca jest oznaczony, nie ukryty. */
export const RouteCell = ({ record }: { record: LooseRecord }) => {
  const origin = [record.fromCountry, record.fromCity].filter(Boolean).join(' ');
  const destination = [record.toCountry, record.toCity].filter(Boolean).join(' ');
  if (!origin && !destination) return <span>—</span>;
  return <span>{`${origin || '?'} → ${destination || '?'}`}</span>;
};

/**
 * Termin ważności z ostrzeżeniem po upływie.
 *
 * Ostrzeżenie, nie zmiana stanu — **stan zmienia człowiek, nie zegar**.
 */
export const ValidUntilCell = ({ record }: { record: LooseRecord }) => {
  const deadline = asText(record.validUntil);
  if (!deadline) return <span>—</span>;
  const expired = isExpired(deadline);
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" color={expired ? 'error' : 'inherit'}>
        {fmtCalendarDate(deadline)}
      </Typography>
      {expired ? (
        <Tooltip title="Termin ważności minął — wymaga decyzji">
          <WarningAmberIcon fontSize="small" color="error" />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/** Kontrahent z widoczną blokadą — planista ma ją zobaczyć przed wejściem w szczegóły. */
export const ContractorCell = ({ record }: { record: LooseRecord }) => {
  const k = asRecord(record.contractor);
  if (!k) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <span>{asText(k.legalName) ?? '—'}</span>
      {k.blacklisted ? (
        <Tooltip title="Kontrahent zablokowany">
          <BlockIcon fontSize="small" color="error" />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/**
 * Liczba żywych kandydatur.
 *
 * Stan „wstępnie planowane" jest zdefiniowany istnieniem kandydatury, więc bez
 * tej kolumny planista widzi stan i nie wie, ile wariantów za nim stoi.
 * Brak wariantów pokazujemy myślnikiem, nie zerem — zero sugeruje policzoną
 * wartość, myślnik mówi „nic tu nie stoi".
 */
export const VariantCountCell = ({ record }: { record: LooseRecord }) => {
  const counts = asRecord(record._count);
  const count = typeof counts?.candidates === 'number' ? counts.candidates : 0;
  if (count === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return <Chip size="small" variant="outlined" label={count} />;
};

/** Numer zlecenia — zewnętrzny, a gdy go nie ma, nasz identyfikator. */
export const OrderNumberCell = ({ record }: { record: LooseRecord }) =>
  <span>{asText(record.externalId) ?? `#${String(record.id)}`}</span>;

/** Numer rejestracyjny przypisanego pojazdu. */
export const Vehicle = ({
  record,
  stacked = false,
}: {
  record: LooseRecord;
  /**
   * Numer pod chipem „Przewoźnik”, a nie obok, i łamany w razie potrzeby —
   * wąska kolumna listy (`CompactVehicleCell`). Smoke stagingu 19.10.2026:
   * od kiedy widok Trade pokazuje „Obsadzone przewoźnikiem” (pyt. 4), chip
   * z numerem w jednej linii rozpychał listę tradera przy 1366 px.
   */
  stacked?: boolean;
}) => {
  const car = asRecord(record.assignedCar);
  if (car) return <span>{asText(car.registrationNumber) ?? '—'}</span>;

  // Zlecenie oddane na zewnątrz: pojazdu nie ma w naszej flocie, bo należy do
  // przewoźnika. Bez tej informacji 933 takie zlecenia wyglądają w banku na
  // lukę w danych, a to normalny ruch podwykonawczy.
  // Chip wielką literą jak pozostałe chipy listy (poprawa brzmienia 01.10.2026).
  const carrier = asRecord(record.carrier);
  const vehicleNumber = asText(record.assignedVehicleNumber);
  if (carrier) {
    return (
      <Tooltip title={`Zlecenie wykonuje przewoźnik: ${asText(carrier.name) ?? '—'}`}>
        <Stack
          direction={stacked ? 'column' : 'row'}
          spacing={stacked ? 0.25 : 0.5}
          sx={{ alignItems: stacked ? 'flex-start' : 'center' }}
          data-testid="carrier-vehicle"
        >
          <Chip size="small" variant="outlined" color="info" label="Przewoźnik" />
          {vehicleNumber && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={stacked ? { overflowWrap: 'anywhere' } : undefined}
            >
              {vehicleNumber}
            </Typography>
          )}
        </Stack>
      </Tooltip>
    );
  }

  // Numer jest, przewoźnika nie ma — nie wiemy, czyj to pojazd.
  if (vehicleNumber) {
    return (
      <Tooltip title="Tego numeru nie ma w kartotece pojazdów, a przewoźnika nie wskazano.">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={stacked ? { overflowWrap: 'anywhere' } : undefined}
        >
          {vehicleNumber}
        </Typography>
      </Tooltip>
    );
  }
  return <span>—</span>;
};

/** Spółka klienta — instancja obsługuje dwie. */
export const CompanyCell = ({ record }: { record: LooseRecord }) => {
  const company = asRecord(record.company);
  return <span>{asText(company?.name) ?? '—'}</span>;
};

/**
 * Od kiedy rekord jest w bieżącym stanie i kto go tam wprowadził (869f1fzha).
 * Dla „przekazane do Trade" to czas i autor przekazania. Ostrzeżenie, gdy API
 * oznaczyło wiersz jako bezczynny — próg jest konfiguracją słownika, więc
 * front go nie liczy, tylko pokazuje wynik.
 */
export const StatusSinceCell = ({ record }: { record: LooseRecord }) => {
  const since = asText(record.planningStatusSince);
  const changedBy = asRecord(record.planningStatusBy);
  const idle = record.idle === true;
  if (!since) return <span>—</span>;
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" color={idle ? 'warning.main' : 'inherit'}>
        {fmtDate(since)}
      </Typography>
      {changedBy?.username ? (
        <Typography variant="caption" color="text.secondary">
          {String(changedBy.username)}
        </Typography>
      ) : null}
      {idle ? (
        <Tooltip title="Bez działania dłużej niż próg dla tego stanu">
          <WarningAmberIcon fontSize="small" color="warning" />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/**
 * Skąd jest rekord.
 *
 * Dane demo są oznaczone W DANYCH (`sourceSystem = 'demo'`, numer `DEMO/…` —
 * `apps/api/src/user-offers/sources.ts`); tu tylko pokazujemy to tak, żeby nie
 * dało się przeoczyć: czerwony chip z napisem, nie sam kod źródła. Wymóg
 * z 08.09.2026: „dane testowe muszą być oznaczone".
 */
export const SourceCell = ({ record }: { record: LooseRecord }) => {
  const source = asText(record.sourceSystem);
  if (isDemo(source)) {
    return (
      <Tooltip title={DEMO_SOURCE_HINT}>
        <Chip size="small" color="error" variant="outlined" label="DEMO — dane testowe" />
      </Tooltip>
    );
  }
  return <span>{sourceLabel(source)}</span>;
};

/**
 * Ikona stanu publikacji w chipie — kształt powtarza kolor i słowo pod
 * chipem (✓ wystawiona, zegar — oczekująca, wykrzyknik — nieudana).
 * Nieznany stan — bez ikony, jak bez koloru.
 */
function exchangeStatusIcon(status: string) {
  if (status === STATUS_POSTED) return <CheckCircleOutlineIcon />;
  if (status === STATUS_PENDING) return <ScheduleIcon />;
  if (status === STATUS_FAILED) return <ErrorOutlineIcon />;
  return undefined;
}

/**
 * Co wisi na giełdzie — kolumna „Giełda” listy banku.
 *
 * Dział Trade musi widzieć stan giełdowy **na liście** — bez tej kolumny
 * trzeba wejść w każde zlecenie osobno, żeby sprawdzić, czy zostało
 * wystawione. Kolumna pokazuje **stan bieżący**: najnowszą publikację dla
 * każdej giełdy osobno (liczy to API, `exchange-status-column.ts`).
 *
 * Od 07.10.2026 (prezentacja 02.10, pkt 9) to osobna kolumna, nie jeden ze
 * „Znaczników”: pod chipem stoi „od kiedy”, a w dymku pełne zdanie — przy
 * nieudanej próbie z powodem. Widzi ją każdy zalogowany (planista też widzi
 * status publikacji — FTL §5, §14); przycisków giełd tu nie ma, są w karcie.
 *
 * Myślnik znaczy „nie ma tego na giełdzie” — tak samo wygląda zlecenie
 * nigdy niewystawione i takie, z którego ofertę zdjęto. Historię wystawień
 * widać w szczegółach zlecenia, z autorem i powodem.
 *
 * Węziej od 08.10.2026 (decyzja użytkownika: kolumna zostaje i mieści się
 * przy 1366 i 1440 px): chip niesie giełdę z ikoną stanu, a stan słowem
 * z „od kiedy” stoi pod nim — „wystawiona od 09:27” (`publicationCaption`).
 * Pełne zdanie z datą i godziną — w dymku, jak dotąd.
 */
export const ExchangeCell = ({ record, now }: { record: LooseRecord; now?: Date }) => {
  const states = exchangeStates(record);
  if (states.length === 0) return <span>—</span>;
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      {states.map((s) => {
        const caption = publicationCaption(s, now);
        const chip = (
          <Chip
            size="small"
            variant="outlined"
            color={publicationColor(s.status)}
            icon={exchangeStatusIcon(s.status)}
            label={s.exchangeLabel}
            // Pełna etykieta („TIMOCOM: Wystawiona”) dla czytnika ekranu —
            // na ekranie stan stoi słowem pod chipem.
            aria-label={publicationLabel(s)}
          />
        );
        // Odnośnik oddaje giełda przy wystawieniu; bez niego chip zostaje
        // zwykłym napisem — nie budujemy adresu z kodu oferty.
        return (
          <Tooltip key={s.exchange} title={publicationHint(s)}>
            <Stack spacing={0} sx={{ alignItems: 'flex-start' }}>
              {s.link ? (
                <a href={s.link} target="_blank" rel="noreferrer">
                  {chip}
                </a>
              ) : (
                chip
              )}
              {caption && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', whiteSpace: 'nowrap', lineHeight: 1.3 }}
                >
                  {caption}
                </Typography>
              )}
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/**
 * Wózek: czynność wózkowa (osobny rekord TMS przy zleceniu głównym) albo
 * zlecenie główne, przy którym są czynności. Czynność bez zlecenia głównego
 * w banku jest oznaczona ostrzeżeniem — nie przypinamy jej na zgadywanie.
 */
export const ForkliftCell = ({ record }: { record: LooseRecord }) => {
  const summary = forkliftSummary(forkliftInfoOf(record));
  if (!summary) return <span>—</span>;
  return (
    <Tooltip title={summary.detail}>
      <Chip
        size="small"
        variant="outlined"
        color={summary.linked ? 'default' : 'warning'}
        icon={summary.linked ? undefined : <WarningAmberIcon />}
        label={summary.label}
      />
    </Tooltip>
  );
};
